import React, { useState, useEffect, useMemo, useRef } from 'react';
import { BeatLoader } from "react-spinners";
import Cookies from 'js-cookie';
import './index.css';

const API_URL = 'https://api.givemetraffic.ru';

function useColumnCount() {
    const [columns, setColumns] = useState(3);
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 768) setColumns(1);
            else if (window.innerWidth < 1200) setColumns(2);
            else setColumns(3);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    return columns;
}

const getSeverityClass = (score) => {
    if (score >= 80) return 'severity-high';
    if (score >= 50) return 'severity-mid';
    return 'severity-low';
};

// --- КОМПОНЕНТЫ МОДАЛОК ---

const CreateTeamModal = ({ isOpen, onClose, realUserId, onTeamCreated }) => {
    const [teamName, setTeamName] = useState('');
    const [loading, setLoading] = useState(false);

    if (!isOpen) return null;

    const handleCreate = async () => {
        if (!teamName.trim()) return;
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/team/create`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Session-Hash': Cookies.get('sessionHash')
                },
                body: JSON.stringify({ owner_id: realUserId, name: teamName })
            });
            if (res.ok) {
                const newTeam = await res.json();
                onTeamCreated(newTeam);
                setTeamName('');
                onClose();
            } else {
                alert('Ошибка создания команды');
            }
        } catch (e) {
            console.error(e);
            alert('Ошибка сети');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal-overlay">
            <div className="modal" style={{ maxWidth: '400px' }}>
                <div className="modal-header">
                    <h2>Создать команду</h2>
                </div>
                <div className="modal-content">
                    <label>Название команды</label>
                    <input
                        type="text"
                        value={teamName}
                        onChange={(e) => setTeamName(e.target.value)}
                        placeholder="Например: Dream Team"
                        className="modal-input"
                    />
                </div>
                <div className="modal-actions">
                    <button className="cancel-btn" onClick={onClose}>Отмена</button>
                    <button className="save-btn" onClick={handleCreate} disabled={loading}>
                        {loading ? 'Создание...' : 'Создать'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const ManageTeamModal = ({ isOpen, onClose, userData, realUserId, onActionSuccess }) => {
    if (!isOpen) return null;

    const isOwner = userData.members?.some(m => m.user_id === realUserId && m.role === 'owner');
    const members = userData.members || [];

    const myRole = useMemo(() => {
        if (userData?.user_id === realUserId) {
            return 'owner';
        }

        const me = userData.members?.find(m => m.user_id === realUserId);
        return me ? me.role : 'user';
    }, [userData, realUserId]);

    // Удалить участника
    const removeMember = async (targetId) => {
        if (!window.confirm('Удалить участника?')) return;
        try {
            await fetch(`${API_URL}/team/${userData.user_id}/member/${targetId}?requester_id=${realUserId}`, {
                method: 'DELETE',
                headers: { 'X-Session-Hash': Cookies.get('sessionHash') }
            });
            onActionSuccess(false); // false = не удаляли всю команду, просто обновили
        } catch (e) { alert('Ошибка'); }
    };

    // Сменить роль
    const changeRole = async (targetId, currentRole) => {
        const newRole = currentRole === 'admin' ? 'user' : 'admin';
        try {
            await fetch(`${API_URL}/team/${userData.user_id}/member/${targetId}/role`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Session-Hash': Cookies.get('sessionHash')
                },
                body: JSON.stringify({ role: newRole, requester_id: realUserId })
            });
            onActionSuccess(false);
        } catch (e) { alert('Ошибка'); }
    };

    // Удалить команду (для владельца)
    const deleteTeam = async () => {
        if (!window.confirm('Вы уверены? Команда будет удалена безвозвратно.')) return;
        try {
            await fetch(`${API_URL}/team/${userData.user_id}?requester_id=${realUserId}`, {
                method: 'DELETE',
                headers: { 'X-Session-Hash': Cookies.get('sessionHash') }
            });
            onActionSuccess(true); // true = команда удалена, нужно свитчиться
        } catch (e) { alert('Ошибка'); }
    };

    // Выйти из команды (для участника)
    const leaveTeam = async () => {
        if (!window.confirm('Выйти из команды?')) return;
        try {
            await fetch(`${API_URL}/team/${userData.user_id}/leave`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Session-Hash': Cookies.get('sessionHash')
                },
                body: JSON.stringify({ user_id: realUserId })
            });
            onActionSuccess(true);
        } catch (e) { alert('Ошибка'); }
    };

    const [isCopied, setIsCopied] = useState(false);

    const inviteLink = `https://t.me/givemetraffic_bot?start=join_team_${userData.user_id}`;

    const handleCopyLink = async () => {
        if (isCopied) return;
        await navigator.clipboard.writeText(inviteLink);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
    };

    return (
        <div className="modal-overlay">
            <div className="modal">
                <div className="modal-header">
                    <h2>{userData.first_name}</h2>
                    <div className="team-name-badge">Команда</div>
                </div>
                <div className="modal-content">
                    {(myRole === 'owner' || myRole === 'admin') && (
                        <div className="invite-wrapper">
                            <p style={{ marginBottom: 16 }} className='sharingDescription'>
                                Отправьте эту ссылку пользователям, чтобы они присоединились к команде.
                            </p>
                            <div className='inviteContainer'>
                                <input type="text" className='inviteLink' value={inviteLink} readOnly />
                                <button
                                    onClick={handleCopyLink}
                                    className={`role-btn ${isCopied && 'copied'}`}
                                >
                                    {isCopied ? 'Скопировано' : 'Копировать'}
                                </button>
                            </div>
                        </div>
                    )}
                    <div className="members-list">
                        {members.map(member => (
                            <div key={member.user_id} className="member-item">
                                <div className="member-info">
                                    <img src={member.photo_url || "https://ui-avatars.com/api/?background=random"} alt="ava" className="member-ava" />
                                    <div className="member-details">
                                        <div className="member-name">{member.first_name}</div>
                                        <div className="member-role">{member.role === 'owner' ? 'Владелец' : (member.role === 'admin' ? 'Админ' : 'Участник')}</div>
                                    </div>
                                </div>
                                <div className="member-actions">
                                    {isOwner && member.user_id !== realUserId && (
                                        <>
                                            <button className="role-btn" onClick={() => changeRole(member.user_id, member.role)}>
                                                {member.role === 'admin' ? 'Разжаловать' : 'Назначить администратором'}
                                            </button>
                                            <button className="remove-btn" onClick={() => removeMember(member.user_id)}>✕</button>
                                        </>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="danger-zone">
                        {isOwner ? (
                            <button className="delete-team-btn" onClick={deleteTeam}>Удалить команду</button>
                        ) : (
                            <button className="leave-team-btn" onClick={leaveTeam}>Выйти из команды</button>
                        )}
                    </div>
                </div>
                <div className="modal-actions">
                    <button className="cancel-btn" onClick={onClose}>Закрыть</button>
                </div>
            </div>
        </div>
    );
};

const PaymentModal = ({ isOpen, onClose, userData, setUserData, userId, plans }) => {
    const [activeTab, setActiveTab] = useState('plans');
    const [selectedPlan, setSelectedPlan] = useState(plans[1]);
    const [loading, setLoading] = useState(false);

    if (!isOpen) return null;

    const handleInvoice = async () => {
        setLoading(true);
        const payload = {
            user_id: userId,
            invoice: Date.now().toString(),
            total: selectedPlan.price,
            plan: selectedPlan.type,
            email: '' // Email не обязателен для генерации ссылки, но может потребоваться бэкенду
        };

        try {
            const res = await fetch(`${API_URL}/cashless_pay`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Session-Hash': Cookies.get('sessionHash')
                },
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                const data = await res.json();
                // Обновляем список счетов пользователя локально
                setUserData(prev => ({
                    ...prev,
                    cashless: [...(prev.cashless || []), data]
                }));
                // Переключаем на таб истории
                setActiveTab('history');
            } else {
                alert('Ошибка при создании счета');
            }
        } catch (e) {
            console.error(e);
            alert('Ошибка сети');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (ts) => {
        if (!ts) return '';
        // Если timestamp в секундах (10 цифр), умножаем на 1000
        const date = new Date(String(ts).length === 10 ? ts * 1000 : Number(ts));
        return date.toLocaleDateString('ru-RU');
    };

    return (
        <div className="modal-overlay">
            <div className="modal">
                <div className="modal-header">
                    <h2>Оплата</h2>
                    <div className="tabs">
                        <button className={`tab-btn ${activeTab === 'plans' ? 'active' : ''}`} onClick={() => setActiveTab('plans')}>Тарифы</button>
                        <button className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`} onClick={() => setActiveTab('history')}>История счетов</button>
                    </div>
                </div>
                <div className="modal-content">
                    {activeTab === 'plans' ? (
                        <div className="plans-wrapper">
                            <div className="plans-list">
                                {plans.map(plan => (
                                    <div
                                        key={plan.type}
                                        className={`plan-card ${selectedPlan.type === plan.type ? 'selected' : ''}`}
                                        onClick={() => setSelectedPlan(plan)}
                                    >
                                        <div className="plan-info">
                                            <h3>{plan.title}</h3>
                                            <p>до {plan.sources} источников</p>
                                            <p>Обновление {plan.interval_desc}</p>
                                        </div>
                                        <div className="plan-price">
                                            {plan.price.toLocaleString('ru-RU')} ₽ / мес
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="payment-actions">
                                <button className="save-btn full-width" onClick={handleInvoice} disabled={loading}>
                                    {loading ? <BeatLoader color="#fff" size={8} /> : `Выписать счёт на ${selectedPlan.price.toLocaleString('ru-RU')} ₽`}
                                </button>
                                <p className="hint-text">Счёт будет сформирован автоматически. Вы сможете скачать его в формате PDF во вкладке "История счетов".</p>
                            </div>
                        </div>
                    ) : (
                        <div className="history-wrapper">
                            {(!userData.cashless || userData.cashless.length === 0) ? (
                                <p className="no-data">История счетов пуста</p>
                            ) : (
                                <div className="invoices-list">
                                    {/* Сортируем: новые сверху */}
                                    {[...userData.cashless].reverse().map((invoice, idx) => (
                                        <div key={idx} className="invoice-item">
                                            <div className="invoice-info">
                                                <div className="invoice-date">от {invoice.date}</div>
                                                <div className="invoice-plan">Счёт #{invoice.invoice_id} | {!invoice.paid ? (`Ожидает оплаты`) : (`Оплачен`)}</div>
                                            </div>
                                            <div className="invoice-actions">
                                                <a href={invoice.pdf} target="_blank" rel="noreferrer" className="download-btn">Скачать PDF</a>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div className="modal-actions">
                    <button className="cancel-btn" onClick={onClose}>Закрыть</button>
                </div>
            </div>
        </div>
    );
};

const SettingsModal = ({ isOpen, onSave, userData, setUserData, realUserId, setIsSettingsOpen, plans }) => {
    const [backupData, setBackupData] = useState(null);
    const [activeTab, setActiveTab] = useState('general');
    const [newSource, setNewSource] = useState('');

    const myRole = useMemo(() => {
        if (userData?.user_id === realUserId) {
            return 'owner';
        }

        const me = userData.members?.find(m => m.user_id === realUserId);
        return me ? me.role : 'user';
    }, [userData, realUserId]);

    // Вычисляем лимит источников для текущего тарифа
    const maxSources = useMemo(() => {
        if (!plans || !userData.plan) return 10; // Fallback, если данные не загрузились
        const currentPlanConfig = plans.find(p => p.type === userData.plan.type);
        return currentPlanConfig ? currentPlanConfig.sources : 10;
    }, [plans, userData.plan]);

    const currentSourcesCount = (userData.sources || []).length;
    const isLimitReached = currentSourcesCount >= maxSources;
    const isRegularUser = myRole !== 'owner' && myRole !== 'admin';

    useEffect(() => {
        if (isOpen) {
            setBackupData(JSON.parse(JSON.stringify(userData)));
            setActiveTab('general');
            setNewSource('');
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleAddSource = () => {
        if (!newSource.trim()) return;

        // Дополнительная проверка на фронте перед добавлением
        if (isLimitReached) {
            alert(`Достигнут лимит источников для вашего тарифа (${maxSources}). Обновите тариф для добавления новых.`);
            return;
        }

        setUserData(prev => ({ ...prev, sources: [...(prev.sources || []), newSource.trim()] }));
        setNewSource('');
    };

    const handleRemoveSource = (index) => {
        setUserData(prev => ({ ...prev, sources: prev.sources.filter((_, i) => i !== index) }));
    };

    return (
        <div className="modal-overlay">
            <div className="modal">
                <div className="modal-header">
                    <h2>Настройки</h2>
                    <div className="tabs">
                        <button className={`tab-btn ${activeTab === 'general' ? 'active' : ''}`} onClick={() => setActiveTab('general')}>Общие</button>
                        <button className={`tab-btn ${activeTab === 'sources' ? 'active' : ''}`} onClick={() => setActiveTab('sources')}>Источники</button>
                    </div>
                    <span className='close-btn' onClick={() => { setUserData(backupData); setIsSettingsOpen(false); }}>❌</span>
                </div>
                <div className="modal-content">
                    {activeTab === 'general' ? (
                        <>
                            <label>Целевая аудитория</label>
                            <textarea disabled={isRegularUser} rows="3" value={userData.audience_prompt || ''} onChange={e => setUserData({ ...userData, audience_prompt: e.target.value })} />
                            <label>Охват (Reach)</label>
                            <textarea disabled={isRegularUser} rows="2" value={userData.reach_prompt || ''} onChange={e => setUserData({ ...userData, reach_prompt: e.target.value })} />
                            <label>Влияние медиабренда (Impact)</label>
                            <textarea disabled={isRegularUser} rows="2" value={userData.impact_prompt || ''} onChange={e => setUserData({ ...userData, impact_prompt: e.target.value })} />
                            <label>Интервал (минут):</label>
                            <select
                                disabled={isRegularUser}
                                value={userData.update_interval || 60}
                                onChange={e => {
                                    const newValue = Number(e.target.value);

                                    // Проверка для тарифа base
                                    if (userData.plan?.type === "base" && (newValue === 15 || newValue === 30)) {
                                        alert("Этот интервал доступен только на более высоком тарифе");
                                        return; // Выходим из функции, не обновляя стейт
                                    }

                                    // Проверка для тарифа mid
                                    if (userData.plan?.type === "mid" && (newValue === 15)) {
                                        alert("Этот интервал доступен только на более высоком тарифе");
                                        return; // Выходим из функции, не обновляя стейт
                                    }

                                    // Если проверка пройдена или тариф не base, обновляем данные
                                    setUserData({ ...userData, update_interval: newValue });
                                }}
                            >
                                <option value="15">15 минут</option>
                                <option value="30">30 минут</option>
                                <option value="60">1 час</option>
                                <option value="120">2 часа</option>
                            </select>

                        </>
                    ) : (
                        <div className="sources-tab">
                            {/* Отображаем счетчик и лимит */}
                            <div style={{ marginBottom: '10px', fontSize: '14px', color: isLimitReached ? '#e74c3c' : '#888' }}>
                                Использовано слотов: <b>{currentSourcesCount}</b> из <b>{maxSources}</b>
                            </div>

                            <div className="sources-list">
                                {(userData.sources || []).map((s, i) => (
                                    <div key={i} className="source-item">
                                        <span className="source-text">{s}</span>
                                        {(myRole === 'owner' || myRole === 'admin') && (
                                            <button className="remove-source-btn" onClick={() => handleRemoveSource(i)}>✕</button>
                                        )}
                                    </div>
                                ))}
                                {(!userData.sources || userData.sources.length === 0) && <p className="no-data">Источников нет</p>}
                            </div>
                            {(myRole === 'owner' || myRole === 'admin') && (
                                <div className="add-source-row">
                                    <input
                                        type="text"
                                        placeholder={isLimitReached ? "Лимит тарифа исчерпан" : "URL или @channel"}
                                        value={newSource}
                                        onChange={e => setNewSource(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && !isLimitReached && handleAddSource()}
                                        disabled={isLimitReached} // Блокируем инпут
                                        style={isLimitReached ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                                    />
                                    <button
                                        className="add-btn"
                                        onClick={handleAddSource}
                                        disabled={isLimitReached} // Блокируем кнопку
                                        style={isLimitReached ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                                    >
                                        Добавить
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div className="modal-actions">
                    <button className="cancel-btn" onClick={() => { setUserData(backupData); setIsSettingsOpen(false); }}>Отмена</button>
                    <button className="save-btn" onClick={async () => {
                        const success = await onSave(userData);
                        if (success) setBackupData(JSON.parse(JSON.stringify(userData)));
                    }}>Сохранить</button>
                </div>
            </div>
        </div>
    );
};

const AboutModal = ({ isOpen, setIsAboutOpen }) => {
    if (!isOpen) return null;

    return (
        <div className="modal-overlay">
            <div className="modal">
                <div className="modal-header">
                    <h2>Об этом сервисе</h2>
                    <span className='close-btn' onClick={() => { setIsAboutOpen(false); }}>❌</span>
                </div>
                <div className="modal-content">
                    <p>Сервис предназначен для получения информации о том, какие инфоповоды принесут вам максимальный трафик</p>
                    <p>Модель: GMT-V1.3-patch0.1</p>
                    <p>Артикул: 3442-MT-2145</p>
                    <a href="https://docs.google.com/document/d/16CugCM6CW-i5XL8a-21zxRyQRBt_jqWvF4cjvJS7ULc/edit?tab=t.0" target="_blank" rel="noreferrer">Инструкция пользователя</a>
                </div>
                <div className="modal-actions">
                    <button className="cancel-btn" onClick={() => { setIsAboutOpen(false); }}>Закрыть</button>
                </div>
            </div>
        </div>
    );
};

const Main = ({ userId, realUserId, userData, setUserData, accounts, setAccounts, setCurrentUserId, plans }) => {
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [isAboutOpen, setIsAboutOpen] = useState(false);
    const [isPaymentOpen, setIsPaymentOpen] = useState(false);

    // Новые стейты для команд
    const [isCreateTeamOpen, setIsCreateTeamOpen] = useState(false);
    const [isManageTeamOpen, setIsManageTeamOpen] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const [filters, setFilters] = useState({ high: true, mid: true, low: true });
    const columnCount = useColumnCount();
    const dropdownRef = useRef(null);

    // --- ЛОГИКА ПРОВЕРКИ ИСТЕЧЕНИЯ ТАРИФА ---
    useEffect(() => {
        if (userData?.plan?.until && !userData.plan_expired) {
            try {
                // Парсим дату из формата DD.MM.YYYY
                const [day, month, year] = userData.plan.until.split('.').map(Number);
                const expiryDate = new Date(year, month - 1, day);
                const today = new Date();

                // Сбрасываем часы для точного подсчета дней
                today.setHours(0, 0, 0, 0);
                expiryDate.setHours(0, 0, 0, 0);

                // Разница в миллисекундах -> в дни
                const diffTime = expiryDate - today;
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                // Если до конца осталось от 1 до 5 дней (включительно)
                if (diffDays > 0 && diffDays <= 5) {
                    alert(`Ваш тариф истекает через ${diffDays} ${getDaysWord(diffDays)}`);
                }
            } catch (e) {
                console.error("Ошибка при проверке даты тарифа", e);
            }
        }
    }, [userData.user_id]); // Срабатывает при смене пользователя или первой загрузке

    // Вспомогательная функция для склонения слова "день"
    const getDaysWord = (n) => {
        const lastDigit = n % 10;
        if (n > 10 && n < 20) return 'дней';
        if (lastDigit === 1) return 'день';
        if (lastDigit >= 2 && lastDigit <= 4) return 'дня';
        return 'дней';
    };

    // Закрытие дропдауна при клике вне
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // 1. Фильтрация и сортировка плоского списка userData.news
    const filteredNews = useMemo(() => {
        const news = userData.news || [];
        return news
            .filter(item => {
                const sClass = getSeverityClass(item.score);
                if (sClass === 'severity-high' && !filters.high) return false;
                if (sClass === 'severity-mid' && !filters.mid) return false;
                if (sClass === 'severity-low' && !filters.low) return false;
                return true;
            })
            .sort((a, b) => (b.score || 0) - (a.score || 0));
    }, [userData.news, filters]);

    // 2. Расчет времени следующего обновления
    const [nextUpdateStr, setNextUpdateStr] = useState('...');
    useEffect(() => {
        const timer = setInterval(() => {
            if (userData.update) {
                setNextUpdateStr("Обновляется...");
                return;
            }
            const lastTime = userData.last_check ? new Date(userData.last_check).getTime() : Date.now();
            const nextTime = new Date(lastTime + (userData.update_interval || 60) * 60000);
            const now = new Date();
            if (now >= nextTime) setNextUpdateStr("Скоро...");
            else setNextUpdateStr(nextTime.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }));
        }, 1000);
        return () => clearInterval(timer);
    }, [userData.last_check, userData.update_interval, userData.update]);

    const updateNow = async () => {
        try {
            await fetch(`${API_URL}/user/${userData.user_id}/parse`, {
                method: 'POST',
                headers: { 'X-Session-Hash': Cookies.get('sessionHash') }
            });
        } catch (e) { alert('Ошибка'); }
    };

    const saveSettings = async (newSettings) => {
        try {
            const res = await fetch(`${API_URL}/settings`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Session-Hash': Cookies.get('sessionHash')
                },
                body: JSON.stringify({ ...newSettings, user_id: userId })
            });
            if (res.ok) {
                alert('Сохранено');
                return true;
            } else {
                alert('Ошибка при сохранении');
                return false;
            }
        } catch (e) {
            alert('Ошибка сети');
            return false;
        }
    };

    // --- ЛОГИКА КОМАНД ---

    const handleTeamCreated = (newTeam) => {
        const newAccountItem = {
            user_id: newTeam.user_id,
            first_name: newTeam.first_name,
            photo_url: newTeam.photo_url,
            type: 'team'
        };
        setAccounts([...accounts, newAccountItem]);
        setCurrentUserId(newTeam.user_id);
    };

    const handleTeamManagementSuccess = (isDeletedOrLeft) => {
        // Обновляем данные пользователя (список мемеберов мог измениться)
        fetch(`${API_URL}/user?user_id=${userData.user_id}&real_user_id=${realUserId}`, {
            headers: { 'X-Session-Hash': Cookies.get('sessionHash') }
        })
            .then(res => res.json())
            .then(data => {
                if (!data.error) setUserData(data);
            });

        if (isDeletedOrLeft) {
            // Удаляем из списка аккаунтов и переключаемся на личный
            setAccounts(accounts.filter(acc => acc.user_id !== userData.user_id));
            setCurrentUserId(realUserId);
            setIsManageTeamOpen(false);
        }
    };

    const handleSwitchAccount = (id) => {
        setCurrentUserId(id);
        setIsDropdownOpen(false);
    };

    const renderHeader = () => {
        const item = userData?.news?.[0];

        const groupKey = item?.created_at
            ? (() => {
                let dateInput = item.created_at;

                // Если это строка, вырезаем "GMT", "Z" и смещения типа "+03:00"
                // Это заставит браузер считать время локальным, не сдвигая его
                if (typeof dateInput === 'string') {
                    dateInput = dateInput
                        .replace(/GMT/g, '')             // Убираем GMT
                        .replace('Z', '')                // Убираем Z (на всякий случай)
                        .replace(/[+-]\d{2}:?\d{2}$/, ''); // Убираем смещения в конце
                }

                const d = new Date(dateInput);

                // Выводим без опции timeZone, используя системное время браузера
                const date = d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
                const time = d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });

                return `Обновлено ${date} ${time}`;
            })()
            : 'Обновление не выполнялось';

        return (
            <div className="group-header">
                {userData.update &&
                    <BeatLoader color="#11192c" size={10} speedMultiplier={0.4} />
                }
                {userData.update ? `Идёт обновление` : `${groupKey}`}
                <div className="divider"></div>
                {(userData.plan?.type !== "base" || !userData.news) && (
                    <button disabled={userData.update} className="refresh-btn" onClick={() => updateNow()}>
                        {userData.update ? <BeatLoader color="#fff" size={5} speedMultiplier={0.4} /> : `🔄 Обновить`}
                    </button>
                )}
            </div>
        );
    };

    const renderMasonry = () => {
        const columns = Array.from({ length: columnCount }, () => []);
        filteredNews.forEach((item, index) => {
            columns[index % columnCount].push(item);
        });

        return (
            <div className="masonry-container">
                {columns.map((colItems, colIndex) => (
                    <div key={colIndex} className="masonry-column">
                        {colItems.map((item, idx) => (
                            <article key={item.source_hash || idx} className={`item ${getSeverityClass(item.score)}`}>
                                <div className="titleWrapper">
                                    <h3><a href={item.link} target="_blank" rel="noreferrer">{item.title}</a></h3>
                                    <a className="itemLink" href={item.link} target="_blank" rel="noreferrer">{item.link}</a>
                                </div>
                                <div className="meta-rows">
                                    <div className="meta-row">
                                        <div className="score">Reach: {item.reach}</div>
                                        <div className="score">Impact: {item.impact}</div>
                                    </div>
                                    <div className="meta-row">
                                        <div className="total-score">{item.score}</div>
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div>
            <header>
                <div className="left">
                    <div className="userInfo" ref={dropdownRef}>
                        <div className="user-wrapper" >
                            <img className="userPic" src={userData.photo_url || `https://ui-avatars.com/api/?name=${userData.first_name}&background=363738&color=909090`} alt="User" />
                            <div className="userdesc">
                                <a className="userName">{userData.first_name}</a>
                                {!userData.plan_expired ? (
                                    <a className="until">Активен до <b>{userData.plan.until}</b></a>
                                ) : (
                                    <a className="until" style={{ color: 'red' }}>Тариф не оплачен</a>
                                )}
                            </div>
                        </div>
                        <div className="left-btn-group">
                            <button className="settings-btn user-dropdown-container" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                                👨🏻‍💻 Аккаунты
                                <svg className={`dropdown-icon ${isDropdownOpen ? 'open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="6 9 12 15 18 9"></polyline>
                                </svg>
                            </button>
                            <button className="settings-btn" onClick={() => setIsAboutOpen(true)}>ℹ️ Об этом сервисе</button>
                            <button className="settings-btn" onClick={() => window.open("https://docs.google.com/document/d/16CugCM6CW-i5XL8a-21zxRyQRBt_jqWvF4cjvJS7ULc/edit?usp=sharing", "_blank", "noreferrer")}>📖 Инструкция</button>
                        </div>
                        {isDropdownOpen && (
                            <div className="account-menu">
                                {accounts.map(acc => (
                                    <div
                                        key={acc.user_id}
                                        className={`account-item ${acc.user_id === userData.user_id ? 'active' : ''}`}
                                        onClick={() => handleSwitchAccount(acc.user_id)}
                                    >
                                        <img src={acc.photo_url || `https://ui-avatars.com/api/?name=${acc.first_name}`} className="account-ava" alt="ava" />
                                        <div className="dropdown-info">
                                            <span>{acc.first_name}</span>
                                            <span className="user-type">{acc.type === 'team' ? 'Команда' : 'Личный'}</span>
                                        </div>
                                    </div>
                                ))}
                                {!userData.plan_expired && !userData.team && (
                                    <div
                                        className="account-item create-team-item"
                                        onClick={() => { setIsDropdownOpen(false); setIsCreateTeamOpen(true); }}
                                    >
                                        <span><span className="plus">+</span> Создать команду</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                <div className="right">
                    {/* Кнопка "Команда" видна только для командных аккаунтов */}
                    {userData.team && (
                        <button className="settings-btn" onClick={() => setIsManageTeamOpen(true)}>👥 Команда</button>
                    )}
                    <button className="settings-btn" onClick={() => setIsPaymentOpen(true)}>💳 Оплата</button>
                    <button disabled={userData.plan_expired} className="settings-btn" onClick={() => setIsSettingsOpen(true)}>⚙ Настройки</button>
                </div>
            </header >

            <main className="container">
                {!userData.plan_expired &&
                    <div className="controls-panel">
                        <div className="legend">
                            <div className={`legend-item ${!filters.high ? 'inactive' : ''}`} onClick={() => setFilters(f => ({ ...f, high: !f.high }))}>
                                <div className="dot green"></div><div className="range"><span className="range-desc">Высокая</span><span className="range-value">(80-100)</span></div>
                            </div>
                            <div className={`legend-item ${!filters.mid ? 'inactive' : ''}`} onClick={() => setFilters(f => ({ ...f, mid: !f.mid }))}>
                                <div className="dot yellow"></div><div className="range"><span className="range-desc">Средняя</span><span className="range-value">(50-79)</span></div>
                            </div>
                            <div className={`legend-item ${!filters.low ? 'inactive' : ''}`} onClick={() => setFilters(f => ({ ...f, low: !f.low }))}>
                                <div className="dot red"></div><div className="range"><span className="range-desc">Низкая</span><span className="range-value">(0-49)</span></div>
                            </div>
                        </div>
                    </div>
                }

                {!userData.plan_expired &&
                    <div className="list-wrapper">
                        {userData && renderHeader()}
                        {filteredNews.length > 0 ? renderMasonry() :
                            <div className="no-data-wrapper">
                                <div className="no-data-desc">
                                    <h1 className="no-data">Новостей нет</h1>
                                    <p className="no-data">Проверьте фильтры выше или добавьте/отредактируйте источники по кнопке ниже</p>
                                </div>
                                <button className="refresh-btn" onClick={() => setIsSettingsOpen(true)}>⚙ Настройки</button>
                            </div>
                        }
                    </div>
                }

                {userData.plan_expired &&
                    <div className="no-data-wrapper">
                        <div className="no-data-desc">
                            <h1 className="no-data">Тариф не оплачен</h1>
                            <p className="no-data">Оплатите тариф по кнопке ниже, чтобы продолжить использование</p>
                        </div>
                        <button className="refresh-btn" onClick={() => setIsPaymentOpen(true)}>💳 Оплата</button>
                    </div>
                }

            </main>

            <SettingsModal
                isOpen={isSettingsOpen}
                onSave={saveSettings}
                userData={userData}
                realUserId={realUserId}
                setUserData={setUserData}
                setIsSettingsOpen={setIsSettingsOpen}
                plans={plans}
            />

            <AboutModal
                isOpen={isAboutOpen}
                setIsAboutOpen={setIsAboutOpen}
            />

            <PaymentModal
                isOpen={isPaymentOpen}
                onClose={() => setIsPaymentOpen(false)}
                userData={userData}
                plans={plans}
                setUserData={setUserData}
                userId={userData.user_id}
            />

            <CreateTeamModal
                isOpen={isCreateTeamOpen}
                onClose={() => setIsCreateTeamOpen(false)}
                realUserId={realUserId}
                onTeamCreated={handleTeamCreated}
            />

            <ManageTeamModal
                isOpen={isManageTeamOpen}
                onClose={() => setIsManageTeamOpen(false)}
                userData={userData}
                realUserId={realUserId}
                onActionSuccess={handleTeamManagementSuccess}
            />
        </div >
    );
};

export default Main;